import { Decoder } from 'jsonous';
import { Maybe } from 'maybeasy';
import { useRef, useState } from 'react';
import { Link } from '../../../../Resource/Types';
import {
  TypeaheadResource,
  TypeaheadState,
  getCurrentSuggestions,
  idle,
  isLoading,
  loading,
} from '../Types';
import { fetchSuggestions } from './Reactions';

export const useTypeaheadSuggestions = <T>(
  endpoint: Maybe<Link>,
  onInputChange: (value: string) => void,
  onBlur: () => void,
  decoder: Decoder<TypeaheadResource<T>>,
  minQueryLength: number = 2,
  debounceMs: number = 500,
) => {
  const [state, setState] = useState<TypeaheadState<T>>(idle());
  const [showSuggestions, setShowSuggestions] = useState(false);
  const debouncer = useRef<NodeJS.Timeout | undefined>(undefined);
  const blurTimer = useRef<NodeJS.Timeout | undefined>(undefined);

  const handleInputChange = (value: string) => {
    onInputChange(value);

    // Clear existing debouncer
    if (debouncer.current) {
      clearTimeout(debouncer.current);
    }

    // Only fetch suggestions if we have enough characters and an endpoint
    if (value.length >= minQueryLength) {
      endpoint.do((link) => {
        setState(loading(value));
        setShowSuggestions(true);

        debouncer.current = setTimeout(() => {
          fetchSuggestions(link, value, setState, decoder);
        }, debounceMs);
      });
    } else {
      setState(idle());
      setShowSuggestions(false);
    }
  };

  const handleSuggestionClick = (suggestion: T) => {
    // Cancel any pending blur operation
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
      blurTimer.current = undefined;
    }

    setShowSuggestions(false);
    setState(idle());
  };

  const handleBlur = () => {
    // Clear any existing blur timer
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
    }

    // Use requestAnimationFrame for better timing and then add a small delay
    // This ensures the blur happens after any click events are processed
    blurTimer.current = setTimeout(() => {
      // Double-check that we should still hide suggestions
      // (in case focus was regained or suggestion was clicked)
      requestAnimationFrame(() => {
        setShowSuggestions(false);
        onBlur();
        blurTimer.current = undefined;
      });
    }, 150);
  };

  const handleFocus = (currentValue: string) => {
    // Cancel any pending blur operation since we're focusing again
    if (blurTimer.current) {
      clearTimeout(blurTimer.current);
      blurTimer.current = undefined;
    }

    // Show suggestions if we have some loaded
    if (state.kind === 'loaded' && currentValue.length >= minQueryLength) {
      setShowSuggestions(true);
    }
  };

  const suggestions = getCurrentSuggestions(state);
  const shouldShowSuggestions = showSuggestions && (suggestions.length > 0 || isLoading(state));

  return {
    suggestions,
    showSuggestions: shouldShowSuggestions,
    isLoading: isLoading(state),
    handleInputChange,
    handleSuggestionClick,
    handleBlur,
    handleFocus,
  };
};
